import { HttpClient, HttpParams } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { APP_CONFIG } from "../../../core/config/app-config";
import { ManagedUser } from "../../users/models/user.models";
import { GroupPerson, ManagedGroup } from "../models/group.models";

interface GroupResponse {
    gmvid: string;
    clientId?: string;
    admins?: string[];
    members?: string[];
    name?: string;
}

interface GroupPersonResponse {
    gmvid?: string;
    fullName?: string;
    username: string;
}

interface UserDetailsResponse {
    firstName?: string;
    familyName?: string;
    fullName?: string;
    displayName?: string;
    profilePicture?: string | null;
    role?: string[];
    tags?: string[];
    contact?: { email?: string; phone?: string; address?: string };
}

interface UserResponse {
    _id?: string;
    gmvid?: string;
    username: string;
    userDetails?: UserDetailsResponse;
}

interface UsersResponse {
    users?: UserResponse[];
}

@Injectable({ providedIn: "root" })
export class GroupsApiService {
    private readonly http = inject(HttpClient);
    private readonly config = inject(APP_CONFIG);
    private readonly groupsUrl = `${this.config.apiBaseUrl}/servauthen/group`;
    private readonly usersUrl = `${this.config.apiBaseUrl}/servauthen/user/all`;

    loadGroups(): Observable<ManagedGroup[]> {
        return this.http
            .get<GroupResponse[]>(this.groupsUrl)
            .pipe(
                map((groups) =>
                    groups
                        .map((group) => this.mapGroup(group))
                        .sort((a, b) => a.name.localeCompare(b.name))
                )
            );
    }

    loadMembers(groupId: string): Observable<GroupPerson[]> {
        return this.loadPeople(`${this.groupsUrl}/${groupId}/members`);
    }

    loadAdmins(groupId: string): Observable<GroupPerson[]> {
        return this.loadPeople(`${this.groupsUrl}/${groupId}/admins`);
    }

    loadCandidateUsers(): Observable<ManagedUser[]> {
        const params = new HttpParams().set("amount", 1000);
        return this.http
            .get<UsersResponse>(this.usersUrl, { params })
            .pipe(
                map((response) =>
                    (response.users ?? [])
                        .map((user) => this.mapUser(user))
                        .sort((a, b) => a.fullName.localeCompare(b.fullName))
                )
            );
    }

    createGroup(name: string): Observable<ManagedGroup> {
        return this.http
            .post<GroupResponse>(this.groupsUrl, { name })
            .pipe(map((group) => this.mapGroup(group)));
    }

    updateGroup(group: ManagedGroup): Observable<unknown> {
        return this.http.patch(`${this.groupsUrl}/${group.gmvid}`, {
            payload: group,
        });
    }

    deleteGroup(groupId: string): Observable<unknown> {
        return this.http.delete(`${this.groupsUrl}/${groupId}`);
    }

    addMembers(groupId: string, usernames: string[]): Observable<unknown> {
        return this.http.post(
            `${this.groupsUrl}/${groupId}/members`,
            usernames
        );
    }

    removeMembers(groupId: string, usernames: string[]): Observable<unknown> {
        return this.http.delete(`${this.groupsUrl}/${groupId}/members`, {
            body: usernames,
        });
    }

    private loadPeople(url: string): Observable<GroupPerson[]> {
        return this.http.get<GroupPersonResponse[]>(url).pipe(
            map((people) =>
                people
                    .map((person) => ({
                        gmvid: person.gmvid ?? person.username,
                        fullName: person.fullName?.trim() || person.username,
                        username: person.username,
                    }))
                    .sort((a, b) => a.fullName.localeCompare(b.fullName))
            )
        );
    }

    private mapGroup(group: GroupResponse): ManagedGroup {
        return {
            gmvid: group.gmvid,
            clientId: group.clientId ?? "",
            admins: group.admins ?? [],
            members: group.members ?? [],
            name: this.cleanGroupName(group.name?.trim() || group.gmvid),
        };
    }

    private cleanGroupName(name: string): string {
        return name.replace(/^@individual\s*/iu, "").trim();
    }

    private mapUser(user: UserResponse): ManagedUser {
        const details = user.userDetails ?? {};
        const firstName = details.firstName?.trim() ?? "";
        const familyName = details.familyName?.trim() ?? "";
        const fullName =
            details.displayName?.trim() ||
            details.fullName?.trim() ||
            [firstName, familyName].filter(Boolean).join(" ") ||
            user.username;
        const email = (details.contact?.email?.trim() || user.username).replace(
            /-[a-f\d]{24}$/iu,
            ""
        );

        return {
            id: user.gmvid || user._id || user.username,
            username: user.username,
            firstName,
            familyName,
            fullName,
            email,
            contactEmail: details.contact?.email?.trim() || user.username,
            phone: details.contact?.phone,
            address: details.contact?.address,
            profilePicture: details.profilePicture ?? null,
            roles: details.role ?? [],
            tags: details.tags ?? [],
        };
    }
}
